import {
  ExclusionReason, BrandAppearance, RefZone, MarkType
} from '../../services/broadcastAdminService'

/**
 * Where a brand card and its drawer get their data.
 *
 * Support and customers now review the same things with the same controls, but
 * against different endpoints — one sees any broadcast, the other only its own
 * published ones. Passing the calls in keeps a single pair of components
 * rather than a near-duplicate set that drifts.
 */
export interface BrandReviewSource {
  getFrameUrl: (customerId: string, broadcastId: string, appearanceId: string) => string
  /**
   * A signed URL for the seekable copy, so an appearance can be watched rather
   * than only looked at. Rejects with status 409 while the copy is still being
   * remuxed, which the player renders as "preparing".
   */
  getPlaybackUrl: (customerId: string, broadcastId: string) => Promise<{
    data: { url: string; expiresAt: string; durationMs: number | null; bytes: number | null }
  }>
  getAppearances: (
    customerId: string, broadcastId: string, brandName: string
  ) => Promise<{ data: BrandAppearance[] }>
  setZone: (
    customerId: string, broadcastId: string, appearanceId: string, zoneCode: string
  ) => Promise<unknown>
  listZones: (broadcastId?: string) => Promise<{ data: RefZone[] }>
  /**
   * Reviewer override of mark_type. OPTIONAL and support-only on purpose:
   * mark_type is a GLOBAL fact about the mark, so a customer must not be able
   * to assert that Long Beach Coast is a sponsor in everyone else's data.
   * Absent on the customer source, so the control simply does not render.
   */
  /**
   * Exclude ONE occurrence. Separate from the brand-level control because a
   * model that reads a real sponsor onto the wrong frame should cost that
   * frame, not the sponsor.
   */
  setAppearanceExclusion?: (
    customerId: string, broadcastId: string, appearanceId: string,
    reason: ExclusionReason | null
  ) => Promise<unknown>
  /**
   * Merge this brand into another. Offered to support AND customers — unlike
   * mark_type, which is a global fact, "these two names are our one sponsor"
   * is a judgement about the customer's own brands.
   */
  mergeBrand?: (
    customerId: string, aliasKey: string, intoKey: string, canonicalName?: string
  ) => Promise<unknown>
  unmergeBrand?: (customerId: string, aliasKey: string) => Promise<unknown>
  setMark?: (
    customerId: string, broadcastId: string, brandName: string, markType: MarkType | null
  ) => Promise<unknown>
}

// Own module because the card, the drawer and the page all need these, and
// importing them from one another makes a cycle that renders `undefined`.
export const REASONS: { value: ExclusionReason; label: string }[] = [
  { value: 'not_a_brand', label: 'Not a brand' },
  { value: 'misread', label: 'Misread' },
  { value: 'own_mark', label: 'Own mark' },
  { value: 'crowd', label: 'Crowd / incidental' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' }
]

export const secs = (ms: number) => {
  const total = Math.round(ms / 1000)
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m < 60) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

/** Area share is stored 0-1; reviewers think in percent of screen. */
export const pct = (share: number | null | undefined) =>
  share == null ? '—' : `${(Number(share) * 100).toFixed(1)}%`

/**
 * Confidence, as whole percent.
 *
 * Separate from pct() because the two live on different scales and a shared
 * formatter would mislead on both. Area share is fractions of a percent — a
 * perimeter board is ~1% of frame — so it needs a decimal. Confidence sits
 * between the 0.70 floor and 1.0, where a decimal implies the model
 * distinguishes 86.3% from 86.4%, which it does not.
 */
export const conf = (c: number | null | undefined) =>
  c == null ? '—' : `${Math.round(Number(c) * 100)}%`

/** Area-seconds are tenths and units; whole numbers would read as 0. */
export const areaSec = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toFixed(2)


/**
 * What each mark_type means, in the words a reviewer or a customer would use.
 *
 * `sponsor` has no chip: it is the default reading of every row, and a badge
 * on the majority tells nobody anything. Only the exceptions are labelled.
 */
export const MARK_LABELS: Record<MarkType, string> = {
  // NOT "Sponsor". Whether something is sponsored is decided by the partner
  // list, not by this field - and this value's own tab is headed Unsponsored,
  // so the old label contradicted the heading above it. What mark_type
  // 'sponsor' actually asserts is "a real commercial brand, as opposed to a
  // league, club, venue or broadcaster ident", and that is exactly what makes
  // a non-partner brand count.
  sponsor: 'Commercial brand',
  competition: 'Competition',
  team: 'Team',
  venue: 'Venue',
  broadcaster: 'Broadcaster',
  unknown: 'Unclassified'
}

export const MARK_STYLES: Record<MarkType, string> = {
  // Slate, not emerald: emerald means SPONSORED across this feature now, and
  // this badge can only ever appear on a brand that is not.
  sponsor: 'bg-slate-50 text-slate-700 border-slate-200',
  competition: 'bg-purple-50 text-purple-700 border-purple-200',
  team: 'bg-blue-50 text-blue-700 border-blue-200',
  venue: 'bg-amber-50 text-amber-700 border-amber-200',
  broadcaster: 'bg-sky-50 text-sky-700 border-sky-200',
  unknown: 'bg-gray-50 text-gray-600 border-gray-200'
}

/** Ordered for the picker: what a reviewer reaches for most, first. */
export const MARK_OPTIONS: MarkType[] =
  ['sponsor', 'competition', 'team', 'venue', 'broadcaster', 'unknown']
